import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, CalendarClock, MapPin, Plus, Trash2, Loader2 } from 'lucide-react';
import { getCheckinStays, type CheckinStay } from '../../services/visitService';
import type { CampingSpot } from '../../types';

interface PastVisitModalProps {
  spot: CampingSpot;
  userId: string;
  onClose: () => void;
  /** Registrer ett opphold. checkInAt/checkedOutAt i ms (lokal middag). */
  onAddStay: (checkInAt: number, checkedOutAt?: number) => Promise<unknown> | void;
  /** Slett oppholdet med gitt startdato (checkInAt-ms). */
  onDeleteStay: (checkInAt: number) => Promise<unknown> | void;
}

// Lokal YYYY-MM-DD for i dag — default og maks (kan ikke registrere fremtiden).
function todayISO(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
// Tolk en YYYY-MM-DD som lokal middag → unngår tidssone-glidning over døgnskille.
function isoToNoonMs(iso: string): number {
  return new Date(`${iso}T12:00:00`).getTime();
}

const PastVisitModal: React.FC<PastVisitModalProps> = ({ spot, userId, onClose, onAddStay, onDeleteStay }) => {
  const { t, i18n } = useTranslation();
  const max = todayISO();
  const [from, setFrom] = useState(max);
  const [to, setTo] = useState('');
  const [stays, setStays] = useState<CheckinStay[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setStays(await getCheckinStays(userId, spot.id));
    } catch {
      setStays([]);
    }
  }, [userId, spot.id]);

  useEffect(() => { void reload(); }, [reload]);

  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  const formatStay = (s: CheckinStay) => {
    const sameDay = !s.checkedOutAt || new Date(s.checkedOutAt).toDateString() === new Date(s.checkInAt).toDateString();
    return sameDay ? fmt(s.checkInAt) : `${fmt(s.checkInAt)} – ${fmt(s.checkedOutAt!)}`;
  };

  const add = async () => {
    if (!from || busy) return;
    const checkInAt = isoToNoonMs(from);
    const checkedOutAt = to ? isoToNoonMs(to) : undefined;
    if (Number.isNaN(checkInAt)) return;
    setBusy(true);
    try {
      await onAddStay(checkInAt, checkedOutAt && checkedOutAt > checkInAt ? checkedOutAt : undefined);
      setTo('');
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (checkInAt: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await onDeleteStay(checkInAt);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Portal til document.body — åpnes fra SpotCard inne i SidebarPanel, som med
  // `.glass-strong` (backdrop-filter) + z-mapOverlay låser `fixed`-barn til sitt
  // eget 2000-lag. Uten portal maler MobileBottomNav (3000) over «Legg til»-knappen.
  return createPortal(
    <div
      className="fixed inset-0 z-[10004] flex items-end justify-center sm:items-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300 max-h-[90dvh] overflow-y-auto"
        style={{ overscrollBehavior: 'contain', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--border-secondary)]" />
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X size={18} />
        </button>

        <div className="px-6 pt-5 pb-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CalendarClock size={22} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight">
                {t('spot.past_visit.title', 'Har du vært her før?')}
              </h2>
              <p className="text-tiny text-[var(--text-muted)] mt-0.5 flex items-center gap-1 truncate">
                <MapPin size={10} className="shrink-0" />
                <span className="truncate">{spot.name}</span>
              </p>
            </div>
          </div>

          <p className="text-tiny text-[var(--text-muted)] mb-4 leading-relaxed">
            {t('spot.past_visit.subtitle', 'Legg til oppholdene dine her — hvert opphold teller på reisetavla og i reisefilmen.')}
          </p>

          {/* Registrerte opphold */}
          {stays === null ? (
            <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-[var(--text-muted)]" /></div>
          ) : stays.length > 0 ? (
            <div className="mb-5">
              <p className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                {t('spot.past_visit.stays_title', 'Registrerte opphold')}
              </p>
              <ul className="flex flex-col gap-1.5">
                {stays.map(s => (
                  <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)]">
                    <span className="text-sm font-bold text-[var(--text-primary)]">{formatStay(s)}</span>
                    <button
                      onClick={() => remove(s.checkInAt)}
                      disabled={busy}
                      className="p-1.5 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-[var(--text-muted)] hover:text-red-500 transition-colors disabled:opacity-40"
                      title={t('spot.past_visit.delete_stay', 'Slett opphold')}
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-tiny text-[var(--text-muted)] mb-5 italic">
              {t('spot.past_visit.no_stays', 'Ingen opphold registrert ennå.')}
            </p>
          )}

          {/* Legg til opphold (fra–til) */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1">
              <label className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                {t('spot.past_visit.date_label', 'Fra-dato')}
              </label>
              <input
                type="date" value={from} max={max}
                onChange={e => setFrom(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--bg-input)] text-[var(--text-primary)] border border-[var(--border-primary)] focus:border-emerald-400 focus:outline-none text-sm font-bold"
              />
            </div>
            <div className="flex-1">
              <label className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                {t('spot.past_visit.to_label', 'Til-dato (valgfri)')}
              </label>
              <input
                type="date" value={to} min={from} max={max}
                onChange={e => setTo(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--bg-input)] text-[var(--text-primary)] border border-[var(--border-primary)] focus:border-emerald-400 focus:outline-none text-sm font-bold"
              />
            </div>
          </div>

          <button
            onClick={add}
            disabled={!from || busy}
            className="w-full py-3 rounded-2xl bg-emerald-500 text-white font-black text-sm uppercase tracking-wider hover:bg-emerald-600 active:scale-[0.98] transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-40 disabled:active:scale-100 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {t('spot.past_visit.add', 'Legg til opphold')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PastVisitModal;
